// src/components/PortalAfiliado/DocumentosAfiliado.tsx
//
// Requer apenas UMA env var:
//   VITE_DOCS_JSON_URL=https://seu-bucket.s3.amazonaws.com/base-didatica/documentos.json

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  FileText, Download, Search, Filter,
  Calendar, HardDrive, Eye, AlertCircle,
  FolderOpen, ChevronDown, ChevronUp,
  RefreshCw, LayoutGrid, List,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Documento {
  id: string;
  name: string;
  type: string;       // string livre — fallback trata tipos desconhecidos
  category: string;   // string livre — fallback trata categorias desconhecidas
  uploadDate: string;
  size: string;
  description?: string;
  status: 'novo' | 'lido';
  url: string;
}

type ViewMode = 'list' | 'grid';

// ─── Config ───────────────────────────────────────────────────────────────────

const DOCS_JSON_URL = import.meta.env.VITE_DOCS_JSON_URL as string;
const PAGE_SIZE = 12;

const TYPE_FALLBACK = { emoji: '📎', color: '#6b7280', bg: '#f3f4f6', label: 'Arquivo' };
const CAT_FALLBACK  = { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200', dot: '#6b7280' };

const typeConfig: Record<string, typeof TYPE_FALLBACK> = {
  PDF:    { emoji: '📄', color: '#ef4444', bg: '#fef2f2', label: 'PDF'    },
  Word:   { emoji: '📝', color: '#3b82f6', bg: '#eff6ff', label: 'Word'   },
  Excel:  { emoji: '📊', color: '#22c55e', bg: '#f0fdf4', label: 'Excel'  },
  Imagem: { emoji: '🖼️', color: '#a855f7', bg: '#faf5ff', label: 'Imagem' },
};

const categoryConfig: Record<string, typeof CAT_FALLBACK> = {
  'Homologação': { bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-200',   dot: '#3b82f6' },
  'Regulamento': { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', dot: '#a855f7' },
  'Template':    { bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-200',  dot: '#22c55e' },
  'Certificado': { bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-200',  dot: '#f59e0b' },
  'Contrato':    { bg: 'bg-red-50',    text: 'text-red-600',    border: 'border-red-200',    dot: '#ef4444' },
};

// Normaliza tipo pelo nome do arquivo se vier valor desconhecido
function inferirTipo(tipo: string, url: string): string {
  if (typeConfig[tipo]) return tipo;
  const ext = url.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'pdf')                        return 'PDF';
  if (['doc','docx'].includes(ext))         return 'Word';
  if (['xls','xlsx','csv'].includes(ext))   return 'Excel';
  if (['png','jpg','jpeg','webp','gif','svg'].includes(ext)) return 'Imagem';
  return tipo; // mantém original — fallback visual cobre
}

// ─── Skeletons ────────────────────────────────────────────────────────────────

const SkeletonRow: React.FC = () => (
  <div className="flex items-center gap-4 px-5 py-4 animate-pulse">
    <div className="w-11 h-11 rounded-xl bg-gray-100 flex-shrink-0" />
    <div className="flex-1 space-y-2">
      <div className="h-3.5 bg-gray-100 rounded-full w-1/3" />
      <div className="h-2.5 bg-gray-100 rounded-full w-1/2" />
      <div className="h-2 bg-gray-100 rounded-full w-1/4" />
    </div>
    <div className="flex gap-2">
      <div className="w-9 h-9 rounded-xl bg-gray-100" />
      <div className="w-20 h-9 rounded-xl bg-gray-100" />
    </div>
  </div>
);

const SkeletonCard: React.FC = () => (
  <div className="bg-white rounded-2xl border border-gray-100 p-4 animate-pulse space-y-3">
    <div className="w-12 h-12 rounded-xl bg-gray-100" />
    <div className="h-3.5 bg-gray-100 rounded-full w-3/4" />
    <div className="h-2.5 bg-gray-100 rounded-full w-full" />
    <div className="h-2 bg-gray-100 rounded-full w-1/2" />
    <div className="flex gap-2 pt-1">
      <div className="flex-1 h-8 rounded-xl bg-gray-100" />
      <div className="flex-1 h-8 rounded-xl bg-gray-100" />
    </div>
  </div>
);

// ─── DocCard ──────────────────────────────────────────────────────────────────

const DocCard: React.FC<{ doc: Documento }> = ({ doc }) => {
  const tCfg = typeConfig[doc.type]     ?? TYPE_FALLBACK;
  const cCfg = categoryConfig[doc.category] ?? CAT_FALLBACK;

  const handleDownload = useCallback(() => {
    const a = document.createElement('a');
    a.href = doc.url;
    a.download = doc.name;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.click();
  }, [doc]);

  return (
    <div className="bg-white rounded-2xl border border-gray-100
      shadow-[0_1px_8px_rgba(0,0,0,0.04)] p-4 flex flex-col gap-3
      hover:shadow-[0_4px_16px_rgba(0,0,0,0.08)] transition-all duration-200">

      <div className="flex items-start justify-between gap-2">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0"
          style={{ background: tCfg.bg }}>
          {tCfg.emoji}
        </div>
        {doc.status === 'novo' && (
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full
            bg-amber-100 text-amber-700 border border-amber-200 flex-shrink-0">
            Novo
          </span>
        )}
      </div>

      <div className="flex-1">
        <p className="text-sm font-semibold text-gray-800 leading-snug line-clamp-2">{doc.name}</p>
        {doc.description && (
          <p className="text-xs text-gray-400 mt-1 line-clamp-2">{doc.description}</p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-[11px] font-medium px-2 py-0.5 rounded-lg border
          ${cCfg.bg} ${cCfg.text} ${cCfg.border}`}>
          {doc.category}
        </span>
        <span className="text-[11px] text-gray-400 flex items-center gap-1">
          <HardDrive className="w-3 h-3" />{doc.size}
        </span>
      </div>

      <div className="flex gap-2 pt-1">
        <button
          onClick={() => window.open(doc.url, '_blank', 'noopener,noreferrer')}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl border
            border-gray-200 text-xs font-medium text-gray-500
            hover:border-gray-300 hover:bg-gray-50 transition-colors">
          <Eye className="w-3.5 h-3.5" /> Ver
        </button>
        <button
          onClick={handleDownload}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-medium
            text-cosmos-900 hover:shadow-gold transition-all"
          style={{ background: 'linear-gradient(135deg,#f4d03f,#d4af37)' }}>
          <Download className="w-3.5 h-3.5" /> Baixar
        </button>
      </div>
    </div>
  );
};

// ─── DocRow ───────────────────────────────────────────────────────────────────

const DocRow: React.FC<{ doc: Documento }> = ({ doc }) => {
  const tCfg = typeConfig[doc.type]         ?? TYPE_FALLBACK;
  const cCfg = categoryConfig[doc.category] ?? CAT_FALLBACK;

  const handleDownload = useCallback(() => {
    const a = document.createElement('a');
    a.href = doc.url;
    a.download = doc.name;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.click();
  }, [doc]);

  return (
    <div className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50/60 transition-colors">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 text-xl"
        style={{ background: tCfg.bg }}>
        {tCfg.emoji}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-gray-800 truncate">{doc.name}</p>
          {doc.status === 'novo' && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full
              bg-amber-100 text-amber-700 border border-amber-200">
              Novo
            </span>
          )}
        </div>
        {doc.description && (
          <p className="text-xs text-gray-400 mt-0.5 truncate">{doc.description}</p>
        )}
        <div className="flex items-center gap-3 mt-1.5 flex-wrap">
          <span className={`text-[11px] font-medium px-2 py-0.5 rounded-lg border
            ${cCfg.bg} ${cCfg.text} ${cCfg.border}`}>
            {doc.category}
          </span>
          <span className="text-[11px] text-gray-400 flex items-center gap-1">
            <HardDrive className="w-3 h-3" />{doc.size}
          </span>
          <span className="text-[11px] text-gray-400 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {new Date(doc.uploadDate).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={() => window.open(doc.url, '_blank', 'noopener,noreferrer')}
          className="p-2 rounded-xl border border-gray-200 text-gray-400
            hover:border-gray-300 hover:bg-gray-50 transition-colors"
          title="Visualizar">
          <Eye className="w-4 h-4" />
        </button>
        <button
          onClick={handleDownload}
          className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-medium
            text-cosmos-900 hover:shadow-gold transition-all"
          style={{ background: 'linear-gradient(135deg,#f4d03f,#d4af37)' }}>
          <Download className="w-3.5 h-3.5" /> Baixar
        </button>
      </div>
    </div>
  );
};

// ─── CategoryGroup ────────────────────────────────────────────────────────────

const CategoryGroup: React.FC<{
  category: string;
  docs: Documento[];
  viewMode: ViewMode;
}> = ({ category, docs, viewMode }) => {
  const [open, setOpen] = useState(true);
  const dot = (categoryConfig[category] ?? CAT_FALLBACK).dot;

  return (
    <div className="bg-white rounded-2xl border border-gray-100
      shadow-[0_1px_8px_rgba(0,0,0,0.05)] overflow-hidden">

      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-5 py-3.5
          hover:bg-gray-50/60 transition-colors text-left">
        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: dot }} />
        <FolderOpen className="w-4 h-4 text-gray-400" />
        <span className="text-sm font-semibold text-gray-700 flex-1">{category}</span>
        <span className="text-xs text-gray-400 mr-2">{docs.length} arquivo{docs.length !== 1 ? 's' : ''}</span>
        {open
          ? <ChevronUp className="w-4 h-4 text-gray-400" />
          : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>

      {open && (
        viewMode === 'list' ? (
          <div className="divide-y divide-gray-50 border-t border-gray-50">
            {docs.map(doc => <DocRow key={doc.id} doc={doc} />)}
          </div>
        ) : (
          <div className="p-4 border-t border-gray-50 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {docs.map(doc => <DocCard key={doc.id} doc={doc} />)}
          </div>
        )
      )}
    </div>
  );
};

// ─── EmptyState ───────────────────────────────────────────────────────────────

const EmptyState: React.FC = () => (
  <div className="bg-white rounded-2xl border border-gray-100
    shadow-[0_1px_8px_rgba(0,0,0,0.05)]
    flex flex-col items-center justify-center py-16 text-center px-6">
    <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center mb-4">
      <FileText className="w-6 h-6 text-gray-300" />
    </div>
    <p className="text-sm font-medium text-gray-600">Nenhum documento encontrado</p>
    <p className="text-xs text-gray-400 mt-1">Ajuste os filtros de busca</p>
  </div>
);

// ─── Main ─────────────────────────────────────────────────────────────────────

const DocumentosAfiliado: React.FC = () => {
  const [documentos, setDocumentos]           = useState<Documento[]>([]);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState<string | null>(null);
  const [searchQuery, setSearchQuery]         = useState('');
  const [categoryFilter, setCategoryFilter]   = useState('all');
  const [typeFilter, setTypeFilter]           = useState('all');
  const [groupByCategory, setGroupByCategory] = useState(true);
  const [viewMode, setViewMode]               = useState<ViewMode>('list');
  const [page, setPage]                       = useState(1);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${DOCS_JSON_URL}?t=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const raw: Documento[] = await res.json();
      // Normaliza tipo na entrada — evita qualquer quebra posterior
      const data = raw.map(doc => ({
        ...doc,
        type: inferirTipo(doc.type, doc.url),
      }));
      setDocumentos(data);
    } catch (err) {
      setError('Não foi possível carregar os documentos. Tente novamente.');
      console.error('[DocumentosAfiliado] fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);
  useEffect(() => setPage(1), [searchQuery, categoryFilter, typeFilter, groupByCategory]);

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return documentos.filter(doc =>
      (!searchQuery || doc.name.toLowerCase().includes(q) || doc.description?.toLowerCase().includes(q))
      && (categoryFilter === 'all' || doc.category === categoryFilter)
      && (typeFilter     === 'all' || doc.type     === typeFilter),
    );
  }, [documentos, searchQuery, categoryFilter, typeFilter]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated  = useMemo(() =>
    filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
  [filtered, page]);

  const grouped = useMemo(() => {
    if (!groupByCategory) return null;
    const map = new Map<string, Documento[]>();
    for (const doc of filtered) {
      const arr = map.get(doc.category) ?? [];
      arr.push(doc);
      map.set(doc.category, arr);
    }
    return map;
  }, [filtered, groupByCategory]);

  const stats = useMemo(() => ({
    total:      documentos.length,
    novos:      documentos.filter(d => d.status === 'novo').length,
    categories: new Set(documentos.map(d => d.category)).size,
  }), [documentos]);

  return (
    <div className="space-y-6">

      {/* HEADER */}
      <div className="flex items-center gap-3.5">
        <div className="relative flex-shrink-0">
          <div className="absolute -inset-1 bg-gold-400/20 blur-[10px] rounded-full" />
          <img src="/icons/doc.png" alt="Base Didática"
            className="relative w-11 h-11 object-contain drop-shadow-[0_2px_10px_rgba(212,175,55,0.45)]" />
        </div>
        <div>
          <h1 className="text-[22px] font-semibold text-gray-900 tracking-tight leading-none">Base Didática</h1>
          <p className="text-xs text-gray-400 mt-1">Acesse a base didática compartilhada pela Federação</p>
        </div>
        <button onClick={fetchDocs} disabled={loading}
          className="ml-auto p-2 rounded-xl border border-gray-200 text-gray-400
            hover:border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-40"
          title="Recarregar">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Total',      value: stats.total,      dot: '#6366f1', icon: FileText    },
          { label: 'Novos',      value: stats.novos,      dot: '#f59e0b', icon: AlertCircle },
          { label: 'Categorias', value: stats.categories, dot: '#a855f7', icon: Filter      },
        ].map(({ label, value, dot, icon: Icon }) => (
          <div key={label} className="bg-white rounded-2xl border border-gray-100 px-4 py-3.5
            shadow-[0_1px_6px_rgba(0,0,0,0.04)] flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: dot + '15' }}>
              <Icon className="w-4 h-4" style={{ color: dot }} />
            </div>
            <div>
              {loading
                ? <div className="h-4 w-6 bg-gray-100 rounded animate-pulse" />
                : <p className="text-lg font-semibold text-gray-900 leading-none">{value}</p>}
              <p className="text-[11px] text-gray-400 mt-0.5">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* FILTERS */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Buscar base didática…"
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl
              text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold-400
              focus:border-transparent transition-all" />
        </div>

        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
            className="pl-9 pr-8 py-2.5 bg-white border border-gray-200 rounded-xl
              text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold-400
              focus:border-transparent transition-all appearance-none cursor-pointer">
            <option value="all">Categoria</option>
            <option value="Homologação">Homologação</option>
            <option value="Regulamento">Regulamento</option>
            <option value="Template">Template</option>
            <option value="Certificado">Certificado</option>
            <option value="Contrato">Contrato</option>
          </select>
        </div>

        <div className="relative">
          <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            className="pl-9 pr-8 py-2.5 bg-white border border-gray-200 rounded-xl
              text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold-400
              focus:border-transparent transition-all appearance-none cursor-pointer">
            <option value="all">Tipo</option>
            <option value="PDF">PDF</option>
            <option value="Word">Word</option>
            <option value="Excel">Excel</option>
            <option value="Imagem">Imagem</option>
          </select>
        </div>

        <div className="flex gap-2">
          <button onClick={() => setGroupByCategory(g => !g)}
            className={`px-3.5 py-2.5 rounded-xl border text-xs font-medium transition-all
              flex items-center gap-1.5
              ${groupByCategory
                ? 'border-gold-400 bg-amber-50 text-amber-700'
                : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'}`}>
            <FolderOpen className="w-4 h-4" />
            <span className="hidden sm:inline">Agrupar</span>
          </button>

          <div className="flex rounded-xl border border-gray-200 overflow-hidden bg-white">
            {(['list', 'grid'] as ViewMode[]).map(m => (
              <button key={m} onClick={() => setViewMode(m)}
                className={`p-2.5 transition-colors
                  ${viewMode === m ? 'bg-gray-100 text-gray-700' : 'text-gray-400 hover:bg-gray-50'}`}
                title={m === 'list' ? 'Lista' : 'Grid'}>
                {m === 'list' ? <List className="w-4 h-4" /> : <LayoutGrid className="w-4 h-4" />}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ERROR */}
      {error && (
        <div className="flex items-center gap-3 px-5 py-4 bg-red-50 border border-red-100
          rounded-2xl text-sm text-red-600">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={fetchDocs}
            className="text-xs font-medium underline underline-offset-2 hover:no-underline">
            Tentar novamente
          </button>
        </div>
      )}

      {/* LOADING */}
      {loading && (
        <div className="bg-white rounded-2xl border border-gray-100
          shadow-[0_1px_8px_rgba(0,0,0,0.05)] overflow-hidden divide-y divide-gray-50">
          {viewMode === 'list'
            ? Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
            : (
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
              </div>
            )}
        </div>
      )}

      {/* GROUPED */}
      {!loading && !error && groupByCategory && grouped && (
        <div className="space-y-4">
          {grouped.size > 0
            ? Array.from(grouped.entries()).map(([cat, docs]) => (
                <CategoryGroup key={cat} category={cat} docs={docs} viewMode={viewMode} />
              ))
            : <EmptyState />}
        </div>
      )}

      {/* FLAT */}
      {!loading && !error && !groupByCategory && (
        <>
          {paginated.length > 0 ? (
            <div className={viewMode === 'list'
              ? 'bg-white rounded-2xl border border-gray-100 shadow-[0_1px_8px_rgba(0,0,0,0.05)] overflow-hidden divide-y divide-gray-50'
              : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'}>
              {paginated.map(doc =>
                viewMode === 'list'
                  ? <DocRow key={doc.id} doc={doc} />
                  : <DocCard key={doc.id} doc={doc} />,
              )}
            </div>
          ) : <EmptyState />}

          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-400">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} de {filtered.length}
              </p>
              <div className="flex gap-2">
                <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
                  className="px-3.5 py-2 text-xs rounded-xl border border-gray-200 bg-white
                    text-gray-600 disabled:opacity-40 hover:bg-gray-50 transition-colors">
                  ← Anterior
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                  .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                    if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) =>
                    p === '...'
                      ? <span key={`e${i}`} className="px-2 py-2 text-xs text-gray-400">…</span>
                      : (
                        <button key={p} onClick={() => setPage(p as number)}
                          className={`w-9 h-9 text-xs rounded-xl border transition-colors
                            ${page === p
                              ? 'border-gold-400 bg-amber-50 text-amber-700 font-semibold'
                              : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}>
                          {p}
                        </button>
                      ),
                  )}
                <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}
                  className="px-3.5 py-2 text-xs rounded-xl border border-gray-200 bg-white
                    text-gray-600 disabled:opacity-40 hover:bg-gray-50 transition-colors">
                  Próxima →
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DocumentosAfiliado;